import {
    addDoc,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    orderBy,
    query,
    updateDoc,
    where,
    writeBatch
} from 'firebase/firestore';
import {
    InventoryOrder,
    InventoryOrderItem,
    inventoryOrdersRef,
    InventoryOrderStatus
} from '@api/InventoryOrders/InventoryOrder';
import InventoryServ from '@api/Inventory/InventoryServ';
import { firestore } from '@api/Firebase/Firebase';
import ActivityLogServ from '@api/ActivityLogs/ActivityLogServ';
import {
    describeInventoryOrderItems,
    describeInventoryOrderPayment,
    describeInventoryOrderStatus
} from '@api/ActivityLogs/InventoryOrderActivity';

export default {
    async saveInventoryOrder(inventoryOrder: InventoryOrder): Promise<InventoryOrder> {
        if (!inventoryOrder.id) {
            const data = await addDoc(inventoryOrdersRef, inventoryOrder);
            inventoryOrder.id = data.id;
        }
        await updateDoc(doc(inventoryOrdersRef, inventoryOrder.id), { ...inventoryOrder });
        return inventoryOrder;
    },
    async getInventoryOrders(
        branchOfficeId?: string,
        distributionCenterId?: string,
        status?: string
    ): Promise<InventoryOrder[]> {
        const inventoryOrders: InventoryOrder[] = [];
        const filtersQuery = [];
        if (branchOfficeId && branchOfficeId !== 'all') {
            filtersQuery.push(where('branchOfficeId', '==', branchOfficeId));
        } else if (distributionCenterId && distributionCenterId !== 'all') {
            filtersQuery.push(where('distributionCenterId', '==', distributionCenterId));
        }
        if (status && status !== 'all') {
            filtersQuery.push(where('status', '==', status));
        }
        filtersQuery.push(orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(query(inventoryOrdersRef, ...filtersQuery));
        if (snapshot.empty) {
            return inventoryOrders;
        }
        snapshot.docs.map((doc: any) => {
            inventoryOrders.push(doc.data() as InventoryOrder);
        });
        return inventoryOrders;
    },
    async getShoppingList(distributionCenterId?: string): Promise<InventoryOrderItem[]> {
        const inventoryOrders: InventoryOrder[] = [];
        let queryDoc = query(inventoryOrdersRef);
        if (distributionCenterId) {
            queryDoc = query(
                inventoryOrdersRef,
                where('distributionCenterId', '==', distributionCenterId),
                where('status', '==', InventoryOrderStatus.requested),
                orderBy('createdAt', 'desc')
            );
        }
        const snapshot = await getDocs(queryDoc);
        if (snapshot.empty) {
            return [];
        }
        snapshot.docs.map((doc: any) => {
            inventoryOrders.push(doc.data() as InventoryOrder);
        });
        const itemsMap: { [inputId: string]: InventoryOrderItem } = {};
        inventoryOrders.forEach((order) => {
            order.items.forEach((item) => {
                const inputId = item.input.id;

                if (inputId) {
                    if (itemsMap[inputId]) {
                        itemsMap[inputId].quantity += item.quantity;
                    } else {
                        itemsMap[inputId] = {
                            ...item,
                            input: { ...item.input }
                        };
                    }
                }
            });
        });
        return Object.values(itemsMap);
    },
    async getInventoryOrder(inventoryOrderId: string): Promise<InventoryOrder> {
        const documentData = await getDoc(doc(inventoryOrdersRef, inventoryOrderId));
        return documentData.data() as InventoryOrder;
    },
    async updateInventoryOrderItems(inventoryOrderId: string, items: InventoryOrderItem[]): Promise<void> {
        // Solo para la bitácora (ver arriba): si falla, se guarda igual.
        let anterior: InventoryOrder | undefined;
        try {
            anterior = await this.getInventoryOrder(inventoryOrderId);
        } catch (error) {
            console.error('No se pudo leer el pedido anterior para la bitácora', error);
        }
        await updateDoc(doc(inventoryOrdersRef, inventoryOrderId), { items });
        try {
            const antes = anterior?.items?.length || 0;
            if (antes !== items.length) {
                await ActivityLogServ.addActivityLog(
                    'inventoryOrder',
                    inventoryOrderId,
                    describeInventoryOrderItems(antes, items.length)
                );
            }
        } catch (error) {
            console.error('No se pudo registrar la edición del pedido de insumos', error);
        }
        return;
    },
    /**
     * Cambia el estado del pedido. Devuelve los avisos de la recepción (por
     * ejemplo "el CEDIS no tenía registro de X") para que la pantalla los muestre.
     *
     * ⚠️ ORDEN DELIBERADO: recibir PRIMERO, marcar el estado DESPUÉS. Al revés,
     * si el ledger fallaba el pedido ya había quedado en "Recibido" y el botón
     * desaparecía: quedaba marcado como recibido sin haber entrado nada al
     * inventario y sin forma de reintentar desde la sucursal.
     */
    async updateStatus(inventoryOrderId: string, status: InventoryOrderStatus): Promise<string[]> {
        // El ACTO de la persona queda registrado (M6, 2026-09-11); el movimiento
        // de stock NO, porque el ledger `inventory-movements` ya lo guarda con su
        // referencia idempotente (D6). Nunca lanza.
        const registrar = async (enMasa = false) => {
            try {
                await ActivityLogServ.addActivityLog(
                    'inventoryOrder',
                    inventoryOrderId,
                    describeInventoryOrderStatus(status, enMasa)
                );
            } catch (error) {
                console.error('No se pudo registrar el cambio de estado del pedido de insumos', error);
            }
        };
        if (status === InventoryOrderStatus.received) {
            // F5 ③: la recepción pasa por el LEDGER del server, que es idempotente
            // por pedido. El `saveInventoryFromOrder` viejo sumaba dos veces si se
            // recibía dos veces (o si corría el reprocesador de órdenes), no
            // esperaba sus escrituras, se saltaba la entrada en silencio cuando el
            // CEDIS no tenía existencia, y no actualizaba `totalUnits`.
            const result = await InventoryServ.receiveInventoryOrder(inventoryOrderId);
            await updateDoc(doc(inventoryOrdersRef, inventoryOrderId), { status });
            await registrar();
            return result?.warnings || [];
        }
        await updateDoc(doc(inventoryOrdersRef, inventoryOrderId), { status });
        await registrar();
        return [];
    },
    async payInventoryOrder(
        inventoryOrderId: string,
        subtotal: number,
        total: number,
        cashAmount: number,
        creditCardAmount: number,
        bankTransferAmount: number
    ): Promise<void> {
        await updateDoc(doc(inventoryOrdersRef, inventoryOrderId), {
            status: InventoryOrderStatus.pickupReady,
            paymentStatus: 'paid',
            subtotal,
            total,
            cashAmount,
            creditCardAmount,
            bankTransferAmount,
            updatedAt: new Date()
        });
        // ⚠️ `inventory-orders` lleva su PROPIO modelo de pago escalar, fuera de
        // `OrderPayments` y sin pruebas (anotado en el plan de auditoría). Hasta
        // que eso se unifique, el renglón es el único rastro de quién lo cobró.
        try {
            await ActivityLogServ.addActivityLog(
                'inventoryOrder',
                inventoryOrderId,
                describeInventoryOrderPayment(total)
            );
        } catch (error) {
            console.error('No se pudo registrar el pago del pedido de insumos', error);
        }
        return;
    },
    /**
     * Cambio de estado en masa (acción del CEDIS). Devuelve un resumen para que la
     * pantalla pueda decir qué NO se pudo hacer, en vez de callarlo.
     *
     * Para "Recibido" se recibe pedido por pedido ANTES de marcar el estado, y solo
     * se marcan los que sí entraron al inventario. Antes se marcaban todos y luego
     * se reprocesaban tragándose los errores en un `console.error`: un pedido podía
     * quedar en "Recibido" sin haber sumado nada. Como el endpoint es idempotente,
     * volver a seleccionar un pedido ya recibido es justo la forma de reintentarlo.
     */
    async bulkUpdateInventoryOrdersStatus(
        orderIds: string[],
        newStatus: InventoryOrderStatus
    ): Promise<{ updated: number; failed: string[]; warnings: string[] }> {
        if (!orderIds || orderIds.length === 0) {
            throw new Error('No se proporcionaron IDs de órdenes');
        }
        let idsToUpdate = orderIds;
        const failed: string[] = [];
        const warnings: string[] = [];

        if (newStatus === InventoryOrderStatus.received) {
            idsToUpdate = [];
            for (const orderId of orderIds) {
                try {
                    const result = await InventoryServ.receiveInventoryOrder(orderId);
                    warnings.push(...(result?.warnings || []));
                    idsToUpdate.push(orderId);
                } catch (error) {
                    console.error(`No se pudo recibir el pedido ${orderId}:`, error);
                    failed.push(orderId);
                }
            }
        }

        if (idsToUpdate.length === 0) {
            return { updated: 0, failed, warnings };
        }

        const MAX_BATCH_SIZE = 500;
        const batches = Math.ceil(idsToUpdate.length / MAX_BATCH_SIZE);
        for (let i = 0; i < batches; i++) {
            const batch = writeBatch(firestore);
            const batchIds = idsToUpdate.slice(i * MAX_BATCH_SIZE, (i + 1) * MAX_BATCH_SIZE);
            batchIds.forEach((orderId) => {
                const orderRef = doc(inventoryOrdersRef, orderId);
                batch.update(orderRef, {
                    status: newStatus,
                    updatedAt: new Date()
                });
            });
            await batch.commit();
            if (i < batches - 1) {
                await new Promise((resolve) => setTimeout(resolve, 100));
            }
        }
        // Un renglón por pedido, con la marca de que fue en masa. ⚠️ Las
        // escrituras del cambio de estado van en lote (hasta 500) y las de la
        // bitácora NO: es una por pedido, en paralelo, fuera del batch. Vale la
        // pena — "se marcaron 40 recibidos de golpe" es justo lo que alguien va a
        // querer reconstruir, y la selección típica es de unos cuantos. Nunca
        // lanza: el cambio de estado ya está hecho.
        try {
            await Promise.all(
                idsToUpdate.map((orderId) =>
                    ActivityLogServ.addActivityLog(
                        'inventoryOrder',
                        orderId,
                        describeInventoryOrderStatus(newStatus, true)
                    )
                )
            );
        } catch (error) {
            console.error('No se pudo registrar el cambio de estado en masa', error);
        }
        return { updated: idsToUpdate.length, failed, warnings };
    },
    async deleteInventoryOrder(inventoryOrderId: string): Promise<void> {
        await deleteDoc(doc(inventoryOrdersRef, inventoryOrderId));
        return;
    }
};
